import { X, type LucideIcon } from "lucide-react-native";
import React, { ReactNode } from "react";
import { Box } from "../../ui/box";
import { ButtonText } from "../../ui/button";
import { HStack } from "../../ui/hstack";
import { Icon } from "../../ui/icon";
import { Pressable } from "../../ui/pressable";
import { Text } from "../../ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipText,
} from "../../ui/tooltip";
import LoadingButton from "../loadingButton";

export interface BulkAction {
  label: string;
  icon: LucideIcon;
  onPress: () => void;
  /** Brand-filled style for the main action. */
  primary?: boolean;
  /** Destructive (red) style. */
  danger?: boolean;
  loading?: boolean;
  disabled?: boolean;
  /** Message shown in a tooltip on hover (e.g. why the action is disabled). */
  tooltip?: string;
}

interface BulkBarProps {
  count: number;
  actions: BulkAction[];
  onClose: () => void;
  /** Optional extra control (e.g. a Menu) rendered after the actions. */
  children?: ReactNode;
}

/** Floating white pill, centered at the bottom, shown when ≥1 row is selected. */
const BulkBar = React.memo(
  ({ count, actions, onClose, children }: BulkBarProps) => {
    if (count === 0) return null;
    return (
      <Box className="items-center z-50 pointer-events-none fixed bottom-6 left-0 right-0">
        <HStack
          space="md"
          className="items-center rounded-2xl bg-background-0 border border-outline-200 px-4 py-2.5 shadow-hard-5 pointer-events-auto"
        >
          <Text size="sm" bold className="text-typography-900 tabular-nums">
            {count} seleccionados
          </Text>
          {actions.map((action) => {
            const button = (
              <LoadingButton
                size="sm"
                action={
                  action.danger
                    ? "negative"
                    : action.primary
                      ? "brand"
                      : "secondary"
                }
                variant={action.primary || action.danger ? "solid" : "outline"}
                rounded="lg"
                onPress={action.onPress}
                startIcon={action.icon}
                loading={action.loading}
                isDisabled={action.disabled}
              >
                <ButtonText>{action.label}</ButtonText>
              </LoadingButton>
            );
            if (!action.tooltip) {
              return <Box key={action.label}>{button}</Box>;
            }
            return (
              <Tooltip
                key={action.label}
                placement="top"
                trigger={(triggerProps) => (
                  <Box {...triggerProps} className="w-fit">
                    {button}
                  </Box>
                )}
              >
                <TooltipContent className="max-w-72">
                  <TooltipText size="sm">{action.tooltip}</TooltipText>
                </TooltipContent>
              </Tooltip>
            );
          })}
          {children}
          <Pressable onPress={onClose} accessibilityLabel="Cerrar">
            <Icon as={X} size="sm" className="text-typography-400" />
          </Pressable>
        </HStack>
      </Box>
    );
  },
);

BulkBar.displayName = "BulkBar";

export default BulkBar;
